import {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import logger from '%app/js/common/services/Logger';
import useDebouncedValue from '../../services/useDebouncedValue';
import { UNUSABLE_REASONS } from './placesLibraryLoader';

const MIN_QUERY_LENGTH = 3;
const REQUEST_DEBOUNCE_MS = 500;
const NO_QUERY = Object.freeze({ text: '' });

function toSuggestion(prediction) {
    const formatting = prediction.structured_formatting;

    return {
        placeId: prediction.place_id,
        description: prediction.description,
        mainText: formatting.main_text || prediction.description,
        secondaryText: formatting.secondary_text || '',
    };
}

function nextHighlightIndex(currentIndex, step, count) {
    if (currentIndex === null) {
        return step > 0 ? 0 : count - 1;
    }
    const nextIndex = currentIndex + step;

    return nextIndex < 0 || nextIndex >= count ? null : nextIndex;
}

function reportFailedStatus(status, statuses, { onUnusable, onQuotaExceeded }) {
    if (status === statuses.ZERO_RESULTS || status === statuses.UNKNOWN_ERROR) {
        return;
    }
    if (status === statuses.REQUEST_DENIED) {
        onUnusable(UNUSABLE_REASONS.keyRejected);
        return;
    }
    if (status === statuses.OVER_QUERY_LIMIT) {
        onQuotaExceeded();
        return;
    }
    logger.warning('usePlaceSuggestions: Google Places suggestions failed', {
        reason: 'suggestions_failed', status,
    });
}

export default function usePlaceSuggestions({
    placesLibrary, onHighlight, onSelect, onUnusable, onQuotaExceeded,
}) {
    const [typedQuery, setTypedQuery] = useState(NO_QUERY);
    const typedQueryRef = useRef(typedQuery);
    typedQueryRef.current = typedQuery;
    const settledQuery = useDebouncedValue(typedQuery, REQUEST_DEBOUNCE_MS);
    const [suggestions, setSuggestions] = useState([]);
    const [activeIndex, setActiveIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const latestRequestRef = useRef(0);
    const failureCallbacksRef = useRef(null);
    failureCallbacksRef.current = { onUnusable, onQuotaExceeded };
    const pointerOnSuggestionRef = useRef(false);

    const autocompleteService = useMemo(
        () => (placesLibrary ? new placesLibrary.AutocompleteService() : null),
        [placesLibrary],
    );

    const clearSuggestions = useCallback(() => {
        setSuggestions([]);
        setActiveIndex(null);
    }, []);

    const discardSuggestions = useCallback(() => {
        latestRequestRef.current += 1;
        setTypedQuery(NO_QUERY);
        setLoading(false);
        clearSuggestions();
    }, [clearSuggestions]);

    useEffect(() => {
        clearSuggestions();
        if (
            autocompleteService === null
            || settledQuery !== typedQueryRef.current
            || settledQuery.text.trim().length < MIN_QUERY_LENGTH
        ) {
            setLoading(false);
            return undefined;
        }

        const requestId = latestRequestRef.current;
        setLoading(true);
        autocompleteService.getPlacePredictions({ input: settledQuery.text }, (predictions, status) => {
            if (requestId !== latestRequestRef.current) {
                return;
            }
            setLoading(false);
            if (status === placesLibrary.PlacesServiceStatus.OK) {
                setSuggestions(predictions.map(toSuggestion));
                return;
            }
            reportFailedStatus(status, placesLibrary.PlacesServiceStatus, failureCallbacksRef.current);
        });

        return () => {
            latestRequestRef.current += 1;
        };
    }, [autocompleteService, clearSuggestions, placesLibrary, settledQuery]);

    const search = useCallback((typedValue) => {
        latestRequestRef.current += 1;
        setTypedQuery({ text: typedValue });
        setActiveIndex(null);
        if (typedValue.trim().length < MIN_QUERY_LENGTH) {
            setLoading(false);
            setSuggestions([]);
        }
    }, []);

    const select = useCallback((suggestion) => {
        discardSuggestions();
        onSelect(suggestion.description, suggestion.placeId);
    }, [discardSuggestions, onSelect]);

    const handleInputBlur = useCallback(() => {
        if (!pointerOnSuggestionRef.current) {
            discardSuggestions();
        }
    }, [discardSuggestions]);

    const handleKeyDown = useCallback((event) => {
        if ((event.key === 'ArrowDown' || event.key === 'ArrowUp') && suggestions.length > 0) {
            event.preventDefault();
            const nextIndex = nextHighlightIndex(activeIndex, event.key === 'ArrowDown' ? 1 : -1, suggestions.length);
            setActiveIndex(nextIndex);
            onHighlight(nextIndex === null ? typedQuery.text : suggestions[nextIndex].description);
        } else if (event.key === 'Enter' && activeIndex !== null) {
            event.preventDefault();
            select(suggestions[activeIndex]);
        } else if (event.key === 'Enter' || event.key === 'Escape') {
            discardSuggestions();
        }
    }, [activeIndex, discardSuggestions, onHighlight, select, suggestions, typedQuery]);

    const getSuggestionHandlers = useCallback(index => ({
        onMouseEnter: () => {
            setActiveIndex(index);
        },
        onMouseLeave: () => {
            pointerOnSuggestionRef.current = false;
            setActiveIndex(null);
        },
        onMouseDown: (event) => {
            event.preventDefault();
            pointerOnSuggestionRef.current = true;
        },
        onMouseUp: () => {
            pointerOnSuggestionRef.current = false;
        },
        onTouchStart: () => {
            pointerOnSuggestionRef.current = true;
        },
        onTouchEnd: () => {
            pointerOnSuggestionRef.current = false;
        },
        onTouchCancel: () => {
            pointerOnSuggestionRef.current = false;
        },
        onClick: (event) => {
            event.preventDefault();
            pointerOnSuggestionRef.current = false;
            select(suggestions[index]);
        },
    }), [select, suggestions]);

    return {
        suggestions,
        activeIndex,
        loading,
        search,
        handleInputBlur,
        handleKeyDown,
        getSuggestionHandlers,
    };
}

export { REQUEST_DEBOUNCE_MS };
